/**
 * Seeded randomness, so the map is the same map every time.
 *
 * WHY
 * ---
 * The layout was seeded with bare `Math.random()`, which meant every page
 * load produced a different arrangement of the same graph. That is not
 * wrong — the topology is identical and the force sim converges to an
 * equally valid embedding — but it costs more than it looks:
 *
 *   - a screenshot cannot be reasoned about. "The angle in that picture"
 *     names a direction in a coordinate frame that only existed in one
 *     browser session, so it cannot be reproduced, reviewed or bugged.
 *   - two people looking at "the map" are not looking at the same
 *     picture, and neither is one person across two visits. Spatial
 *     memory — "leg locks are over on the right" — never accumulates.
 *   - a layout regression cannot be seen. If a change makes the
 *     arrangement worse, the diff is invisible underneath the noise of
 *     it being different anyway.
 *
 * Seeding costs nothing at runtime and turns all three around. The
 * arrangement becomes a thing that was chosen once rather than rolled
 * fresh for every visitor.
 *
 * MULBERRY32
 * ----------
 * 32-bit state, one multiply-xor round, uniform on [0,1). Not
 * cryptographic and does not need to be — the requirement is "the same
 * sequence every time, cheaply", not unpredictability. Chosen over
 * seeding Math.random (impossible in JS) and over pulling in a library
 * for eight lines.
 *
 * A FRESH GENERATOR PER PASS, NOT ONE SHARED
 * ------------------------------------------
 * This returns a generator rather than exporting a seeded singleton, and
 * callers must create one at the START of each layout pass.
 *
 * A module-level generator would give determinism across reloads and
 * nowhere else: the seed pass re-runs whenever the graph, the cluster
 * axis or a filter changes, and each re-run would continue the stream
 * from wherever the last one stopped. Switching cluster axis and
 * switching back would then produce a different layout than the one you
 * left, which is exactly the property this file exists to remove — and
 * it would look like a bug in clustering rather than a bug in here.
 */

/** The arrangement. Changing this rolls a new map for everyone. */
export const LAYOUT_SEED = 0x5eed1;

/**
 * mulberry32 — returns a function producing uniform floats in [0,1).
 *
 * `>>> 0` throughout keeps the state an unsigned 32-bit integer;
 * JavaScript bitwise operators produce signed 32-bit values, and letting
 * the state go negative changes the sequence.
 */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A point on a sphere of radius `r`, uniformly distributed over the
 * surface.
 *
 * Two callers needed this and both had it inline; the starfield had a
 * correct version and the layout had the same one again. It is worth
 * one shared function because the correctness is not obvious: `phi`
 * comes through `acos(2v - 1)` rather than being drawn uniformly,
 * because uniform latitude bunches points at the poles.
 */
export function spherePoint(
  rand: () => number,
  r: number,
): [number, number, number] {
  const theta = rand() * Math.PI * 2;
  const phi = Math.acos(2 * rand() - 1);
  const s = Math.sin(phi);
  return [r * s * Math.cos(theta), r * s * Math.sin(theta), r * Math.cos(phi)];
}
